import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TAN = '#f0e6d2';
const BLACK = '#000000';

interface LoadingBarsProps {
  /** Show the "LOADING" label below the spinner. Defaults to true. */
  showLabel?: boolean;
  /** Visual size of the spinner. Defaults to 'md'. */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Reusable loader: a black arc tracing a circle on the tan brand surface.
 * Used by LoadingScreen, Layout, ProtectedRoute, and Suspense fallbacks.
 */
export const LoadingBars: React.FC<LoadingBarsProps> = ({ showLabel = true, size = 'md' }) => {
  const dim = size === 'sm'
    ? { px: 36, stroke: 3, label: 'text-[10px] mt-5 tracking-[0.4em]' }
    : size === 'lg'
    ? { px: 88, stroke: 4, label: 'text-sm mt-10 tracking-[0.5em]' }
    : { px: 52, stroke: 3.5, label: 'text-xs mt-8 tracking-[0.45em]' };

  const { px, stroke } = dim;
  const center = px / 2;
  const radius = center - stroke;

  return (
    <div className="flex flex-col items-center justify-center select-none">
      <svg
        width={px}
        height={px}
        viewBox={`0 0 ${px} ${px}`}
        className="animate-spin"
        style={{ animationDuration: '0.85s' }}
        aria-hidden
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="rgba(0, 0, 0, 0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={BLACK}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${radius * 1.85} ${radius * 4.4}`}
        />
      </svg>
      {showLabel && (
        <p
          className={`font-semibold uppercase ${dim.label}`}
          style={{ color: BLACK, opacity: 0.55 }}
        >
          Loading
        </p>
      )}
    </div>
  );
};

interface LoadingScreenProps {
  /**
   * When true (the default), the loader auto-navigates to "/" after a short
   * delay. Use this for the post-login intro route. Set to false anywhere
   * the loader is just a Suspense fallback or in-flight indicator.
   */
  autoNavigate?: boolean;
  /** ms before auto-navigation when autoNavigate is true. */
  autoNavigateDelayMs?: number;
}

/**
 * Full-screen loading panel on the brand tan surface. Used at /intro and as
 * the universal Suspense / route-loading fallback throughout the app.
 */
const LoadingScreen: React.FC<LoadingScreenProps> = ({
  autoNavigate = true,
  autoNavigateDelayMs = 2000,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!autoNavigate) return;

    import('../pages/Dashboard').catch(() => {});
    import('../pages/affiliate-orders/AffiliateOrdersPage').catch(() => {});

    const t = setTimeout(() => navigate('/'), autoNavigateDelayMs);
    return () => clearTimeout(t);
  }, [navigate, autoNavigate, autoNavigateDelayMs]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: TAN }}
    >
      <LoadingBars size="md" />
    </div>
  );
};

export default LoadingScreen;
